import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { LogOut, ArrowUpRight, Package, Repeat } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import OrderTimeline from "../components/OrderTimeline";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Account() {
  const { user, logout } = useAuth();
  const [orders, setOrders] = useState(null); // null = loading
  const [subs, setSubs] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/user/orders`);
        if (!cancelled) setOrders(data.orders || []);
      } catch (e) {
        if (!cancelled) setOrders([]);
      }
    })();
    (async () => {
      try {
        const { data } = await axios.get(`${API}/subscriptions/mine`);
        if (!cancelled) setSubs(data.subscriptions || []);
      } catch {
        if (!cancelled) setSubs([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const cancelSub = async (sub) => {
    if (!window.confirm("Cancel this subscription at the end of the period?")) return;
    try {
      await axios.post(`${API}/subscriptions/${sub.stripe_subscription_id}/cancel`);
      toast.success("Cancellation scheduled");
      setSubs((s) => s.map((x) =>
        x.stripe_subscription_id === sub.stripe_subscription_id
          ? { ...x, cancel_at_period_end: true }
          : x
      ));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cancel failed");
    }
  };

  const doLogout = async () => {
    await logout();
    toast.success("Signed out.");
    window.location.href = "/";
  };

  const first = (user?.name || "").split(" ")[0] || "Friend";

  return (
    <div
      data-testid="account-page"
      className="min-h-screen bg-[color:var(--sr-bg)] pt-32 md:pt-40 pb-24"
    >
      <div className="sr-container">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9 }}
          className="grid grid-cols-1 lg:grid-cols-12 gap-16"
        >
          {/* Sidebar */}
          <aside className="lg:col-span-4">
            <p className="label-eyebrow text-[color:var(--sr-primary)] mb-6 flex items-center gap-3">
              <span className="w-8 h-px bg-[color:var(--sr-accent)]" /> Your account
            </p>
            <div className="flex items-center gap-4 mb-8">
              {user?.picture ? (
                <img
                  src={user.picture}
                  alt=""
                  className="w-14 h-14 rounded-full object-cover border hairline"
                  referrerPolicy="no-referrer"
                />
              ) : (
                <div className="w-14 h-14 rounded-full bg-[color:var(--sr-secondary)] border hairline flex items-center justify-center font-serif text-2xl text-[color:var(--sr-primary)]">
                  {first[0]?.toUpperCase() || "R"}
                </div>
              )}
              <div>
                <p
                  data-testid="account-name"
                  className="font-serif text-2xl text-[color:var(--sr-primary)] leading-tight"
                >
                  {user?.name || "Friend"}
                </p>
                <p
                  data-testid="account-email"
                  className="text-xs text-[color:var(--sr-muted)] mt-1"
                >
                  {user?.email}
                </p>
              </div>
            </div>
            <div className="space-y-3 border-t hairline-light pt-6">
              <Link
                to="/shop"
                className="flex items-center justify-between label-eyebrow text-[color:var(--sr-primary)] hover:text-[color:var(--sr-accent)] transition-colors"
              >
                Continue shopping <ArrowUpRight size={14} strokeWidth={1.25} />
              </Link>
              <Link
                to="/markets"
                className="flex items-center justify-between label-eyebrow text-[color:var(--sr-primary)] hover:text-[color:var(--sr-accent)] transition-colors"
              >
                Farmers markets <ArrowUpRight size={14} strokeWidth={1.25} />
              </Link>
              {user?.is_admin && (
                <>
                  <Link
                    to="/admin/products"
                    data-testid="account-admin-link"
                    className="flex items-center justify-between label-eyebrow text-[color:var(--sr-primary)] hover:text-[color:var(--sr-accent)] transition-colors"
                  >
                    Manage products <ArrowUpRight size={14} strokeWidth={1.25} />
                  </Link>
                  <Link
                    to="/admin/orders"
                    data-testid="account-admin-orders-link"
                    className="flex items-center justify-between label-eyebrow text-[color:var(--sr-primary)] hover:text-[color:var(--sr-accent)] transition-colors"
                  >
                    Manage orders <ArrowUpRight size={14} strokeWidth={1.25} />
                  </Link>
                  <Link
                    to="/admin/reviews"
                    data-testid="account-admin-reviews-link"
                    className="flex items-center justify-between label-eyebrow text-[color:var(--sr-primary)] hover:text-[color:var(--sr-accent)] transition-colors"
                  >
                    Manage reviews <ArrowUpRight size={14} strokeWidth={1.25} />
                  </Link>
                </>
              )}
              <button
                type="button"
                data-testid="logout-btn"
                onClick={doLogout}
                className="w-full flex items-center justify-between label-eyebrow text-[color:var(--sr-text)]/75 hover:text-[color:var(--sr-primary)] pt-3 transition-colors"
              >
                Sign out <LogOut size={14} strokeWidth={1.25} />
              </button>
            </div>
          </aside>

          {/* Orders column */}
          <div className="lg:col-span-8">
            <h1 className="font-serif text-5xl md:text-7xl leading-[0.9] tracking-tight text-[color:var(--sr-primary)]">
              Hello, {first}.
              <br />
              <em className="italic font-light text-[color:var(--sr-accent)]">
                What you&apos;ve collected.
              </em>
            </h1>

            {/* Active subscriptions */}
            {subs?.length > 0 && (
              <div className="mt-14 border-t hairline-light pt-10" data-testid="account-subs">
                <p className="label-eyebrow text-[color:var(--sr-primary)] mb-6 flex items-center gap-2">
                  <Repeat size={14} strokeWidth={1.25} /> The Ritual — active
                </p>
                <ul className="divide-y divide-[color:var(--sr-border-light)]">
                  {subs.map((s) => (
                    <li
                      key={s.stripe_subscription_id}
                      className="py-5 flex items-start justify-between gap-4"
                      data-testid={`sub-row-${s.stripe_subscription_id}`}
                    >
                      <div>
                        <p className="font-serif text-xl text-[color:var(--sr-primary)] leading-tight">
                          {s.lookup_key === "sr_sub_weekly"
                            ? "The Ritual · Weekly"
                            : "The Ritual · Monthly"}
                        </p>
                        <p className="text-xs text-[color:var(--sr-muted)] mt-1">
                          {s.cancel_at_period_end ? (
                            <span className="text-[color:var(--sr-accent)]">
                              Cancels at period end
                            </span>
                          ) : s.current_period_end ? (
                            <>
                              Next delivery{" "}
                              {new Date(
                                s.current_period_end * 1000
                              ).toLocaleDateString(undefined, {
                                month: "long",
                                day: "numeric",
                              })}
                            </>
                          ) : (
                            <>Active</>
                          )}
                        </p>
                      </div>
                      {!s.cancel_at_period_end && (
                        <button
                          type="button"
                          onClick={() => cancelSub(s)}
                          data-testid={`cancel-sub-${s.stripe_subscription_id}`}
                          className="label-eyebrow text-[color:var(--sr-muted)] hover:text-[color:var(--sr-primary)] py-2 px-1 min-h-[44px] flex items-center"
                        >
                          Cancel
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="mt-14 border-t hairline-light pt-10">
              <p className="label-eyebrow text-[color:var(--sr-primary)] mb-8 flex items-center gap-3">
                <Package size={14} strokeWidth={1.25} /> Orders
              </p>

              {orders === null && (
                <p className="text-sm text-[color:var(--sr-muted)]">Loading…</p>
              )}

              {orders?.length === 0 && (
                <div
                  data-testid="no-orders"
                  className="border hairline p-8 md:p-12 text-center"
                >
                  <p className="font-serif text-3xl md:text-4xl text-[color:var(--sr-primary)] leading-tight">
                    No bottles yet.
                    <br />
                    <em className="italic font-light text-[color:var(--sr-accent)]">
                      A first pour awaits.
                    </em>
                  </p>
                  <Link
                    to="/shop"
                    data-testid="empty-orders-shop-link"
                    className="mt-8 inline-flex items-center gap-3 px-8 py-4 bg-[color:var(--sr-primary)] text-[color:var(--sr-secondary)] label-eyebrow hover:bg-[color:var(--sr-primary-deep)] transition-colors"
                  >
                    Discover the collection
                    <ArrowUpRight size={16} strokeWidth={1.25} />
                  </Link>
                </div>
              )}

              {orders?.length > 0 && (
                <ul
                  className="divide-y divide-[color:var(--sr-border-light)]"
                  data-testid="orders-list"
                >
                  {orders.map((o) => (
                    <li key={o.id} className="py-8" data-testid={`order-row-${o.id}`}>
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <p className="font-serif text-xl text-[color:var(--sr-primary)] leading-tight">
                            Order #{(o.id || "").slice(-8)}
                          </p>
                          <p className="text-xs text-[color:var(--sr-muted)] mt-1">
                            {new Date(o.created_at).toLocaleDateString(undefined, {
                              year: "numeric",
                              month: "long",
                              day: "numeric",
                            })}
                            {" · "}
                            {(o.items || []).reduce((n, i) => n + (i.quantity || 0), 0)}{" "}
                            items
                            {o.fulfillment === "pickup" && (o.market || {}).name && (
                              <>
                                {" · "}
                                <span className="text-[color:var(--sr-accent)]">
                                  Pickup at {(o.market || {}).name}
                                </span>
                              </>
                            )}
                          </p>
                        </div>
                        <p className="font-serif text-2xl text-[color:var(--sr-primary)] tabular-nums">
                          ${(o.total || 0).toFixed(2)}
                        </p>
                      </div>
                      <OrderTimeline order={o} />
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
